import { useEffect, useState } from 'react';
import { Plane, ExternalLink } from 'lucide-react';
import { DOWNLOAD_URL, IS_PRODUCTION } from '../../constants/links';

/**
 * Landing page shown when someone scans a QR code but doesn't have the app / isn't logged in.
 * Attempts to open the native app via rostfound:// scheme, then shows TestFlight download.
 */
export function AddFriendLanding({ handle }: { handle: string }) {
  const [showDownload, setShowDownload] = useState(false);

  // Persist handle so it survives install + signup
  useEffect(() => {
    localStorage.setItem('rostfound_pending_add', handle);
  }, [handle]);

  // Try to open the native app via custom scheme, show download quickly if it fails
  useEffect(() => {
    const timeout = setTimeout(() => setShowDownload(true), 800);
    window.location.href = `rostfound://add/${handle}`;
    return () => clearTimeout(timeout);
  }, [handle]);

  return (
    <div className="min-h-dvh bg-slate-900 flex flex-col items-center justify-center px-6 text-center">
      {/* Logo */}
      <div className="w-20 h-20 rounded-2xl bg-accent/15 border border-accent/30 flex items-center justify-center mb-6">
        <Plane size={36} className="text-accent" />
      </div>

      <h1 className="text-2xl font-bold text-slate-100 mb-2">Rost & Found</h1>
      <p className="text-slate-400 text-sm mb-6 max-w-xs">
        <span className="text-accent font-semibold">@{handle}</span> wants to connect with you.
        {IS_PRODUCTION
          ? ' Download the app to see schedule overlaps and plan meetups with crew.'
          : ' Join the beta to see schedule overlaps and plan meetups with crew.'}
      </p>

      {/* Download / TestFlight button — always visible but animates in */}
      <a
        href={DOWNLOAD_URL}
        className={`flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-accent text-white font-semibold text-base active:bg-accent-dark active:scale-[0.98] transition-all mb-3 w-full max-w-xs ${showDownload ? 'opacity-100' : 'opacity-0'}`}
      >
        <ExternalLink size={18} />
        {IS_PRODUCTION ? 'Download on the App Store' : 'Join Beta on TestFlight'}
      </a>

      {!IS_PRODUCTION && showDownload && (
        <p className="text-xs text-slate-500 mb-4 max-w-xs">
          After installing, open the app — <span className="text-accent">@{handle}</span> will be added automatically.
        </p>
      )}

      {/* Login link */}
      <button
        onClick={() => {
          window.history.replaceState(null, '', '/');
          window.location.reload();
        }}
        className="text-sm text-accent/70 hover:text-accent transition-colors"
      >
        Already have an account? Log in
      </button>
    </div>
  );
}
